import type { GeoArea, GeoLine, GeoPt, MapObject, PinFolder } from '@wehunt/ui'
import { LINE_DEFAULT_WIDTH, hexToArgbInt } from '@wehunt/ui'
import {
  createHuntingGround,
  updateHuntingGround,
  deleteHuntingGround,
  type HuntingGroundAreaDto,
} from './api/huntingGroundsApi'
import {
  createTeamMapPin,
  updateTeamMapPin,
  copyTeamMapPinImages,
  deleteTeamMapPin,
  fileTeamMapPin,
  type TeamMapPinDto,
} from './api/teamMapPinsApi'
import { createTeamMapLine, updateTeamMapLine, deleteTeamMapLine } from './api/teamMapLinesApi'
import {
  createTeamPinFolder,
  renameTeamPinFolder,
  deleteTeamPinFolder,
} from './api/teamPinFoldersApi'
import { uploadMapPinImage, deleteMapPinImage } from './mapPinImages'
import { geoLineToWriteBody } from './mapLineDtoMappers'
import { dtoToGeoRings, isPrivatePin, isTrailCameraPin } from './mapDtoMappers'
import { runPooled, withRetry, type RetryOptions } from '../../shared/api/batch'
import { ApiError } from '../../shared/api/ApiError'

/**
 * Max map-save writes in flight at once. Deliberately small: a hunting-ground
 * map can hold many areas + pins, and firing every create/update/delete
 * simultaneously overwhelmed the API gateway, which replied 502 and silently
 * dropped writes → partial saves (CLUB-195).
 */
export const MAP_SAVE_CONCURRENCY = 4

type SaveTask = () => Promise<unknown>

// The colour a hunt border falls back to when nothing has chosen one. A stored null
// renders identically to it, so the two are equal when diffing — otherwise a
// colour-less border looks "changed" on every save and gets needlessly re-PUT, which
// also 400s any area whose stored polygon is degenerate (<3 coords).
//
// The editor no longer SENDS this value — it stopped stamping a default on load, and a
// hunt border's colour is the team's field now — but another client still can, and this
// unit test suite calls saveTeamMap directly with it. Equality here is a statement
// about what the value means, not a workaround for one caller.
const DEFAULT_BORDER_COLOR = '#3e68ff'
const normColor = (c: string | null | undefined): string => {
  const v = (c ?? '').toLowerCase()
  return v === DEFAULT_BORDER_COLOR ? '' : v
}


export function hasAreaChanged(
  current: { name: string; rings: GeoPt[][]; color?: string | null; areaSource?: string | null; areaGroup?: string | null },
  original: HuntingGroundAreaDto,
): boolean {
  if (current.name !== (original.name ?? '')) return true
  // An absent colour is not a request to clear one: the API preserves the stored value
  // when the payload carries none, so an area that simply does not carry a colour can
  // never be the thing that changed. Diffing it as a change would re-PUT every
  // colourless area on every save, for a write the API is going to ignore.
  if (current.color != null && normColor(current.color) !== normColor(original.color)) return true
  // A parcel's provenance/grouping changing (e.g. re-sourced from properties)
  // must re-PUT the record. Treat null and undefined as the same "unset".
  if ((current.areaSource ?? null) !== (original.areaSource ?? null)) return true
  if ((current.areaGroup ?? null) !== (original.areaGroup ?? null)) return true
  // Every ring, not just the first. Comparing one ring reported "unchanged" for a
  // multi-part ground whose other parts had been dropped, so the save that would
  // have restored them was never sent.
  //
  // The stored rings go through the SAME mapper the editor's rings came from, rather
  // than being read raw off the DTO. The editor works in open rings; a cadastral
  // parcel is stored closed, because the property service states the outline that way
  // and a GIS ring is closed by specification. Comparing an open ring against a closed
  // one reports every property-sourced parcel as edited on every save — a needless PUT
  // per parcel, against an API that answers 502 when a large map fires its writes at
  // once, and each of those writes would rewrite the stored outline.
  const originalRings = dtoToGeoRings(original)
  if (current.rings.length !== originalRings.length) return true
  return current.rings.some((ring, r) => {
    const was = originalRings[r]
    if (ring.length !== was.length) return true
    return ring.some(
      (p, i) =>
        Math.abs(p.lat - was[i].lat) > 1e-8 ||
        Math.abs(p.lon - was[i].lon) > 1e-8,
    )
  })
}

/** Build the (deferred) create/update/delete tasks for hunting-ground areas. */
export function buildAreaSaveTasks(
  groupId: string,
  state: { boundaryAreas: GeoArea[]; subAreaPolygons: GeoArea[]; forbiddenPolygons: GeoArea[] },
  originalAreas: HuntingGroundAreaDto[],
  getToken: () => Promise<string>,
): SaveTask[] {
  // Diff each area type against ONLY the originals of the SAME type. Area ids
  // are not unique across types — a hunt border and a forbidden/sub area (drawn,
  // imported, or created on mobile) can share an id — and matching by id alone
  // let a forbidden area's UPDATE stamp its type onto a border's record, or a
  // delete-by-id remove a border still present under another type. Either erased
  // the boundary and it stayed gone (CLUB-505). Per-type scoping makes a save
  // unable to touch a record of a different type.
  const groups: { areaType: 'hunt' | 'sub' | 'forbidden'; current: GeoArea[] }[] = [
    { areaType: 'hunt', current: state.boundaryAreas },
    { areaType: 'sub', current: state.subAreaPolygons },
    { areaType: 'forbidden', current: state.forbiddenPolygons },
  ]

  const tasks: SaveTask[] = []

  for (const { areaType, current } of groups) {
    const originals = originalAreas.filter((a) => a.areaType === areaType)
    const originalIds = new Set(originals.map((a) => a.id))
    const currentIds = new Set(current.map((a) => a.id))

    for (const area of current) {
      const rings = area.rings.map((ring) =>
        ring.map((p) => ({ latitude: p.lat, longitude: p.lon })),
      )
      const body = {
        name: area.name || null,
        areaType,
        // Both shapes, for the same reason the read side emits both: an API deployed
        // from the previous tag reads only `coordinates`, and a body without it would
        // be rejected as having no outline. The first ring is what such an API would
        // have received before this change, so nothing about its behaviour changes.
        coordinates: rings[0] ?? [],
        rings,
        // Absent on the area means every ring is an outer — the same default the read
        // side applies — so the API is never left to guess.
        outerRingCount: area.outerCount ?? rings.length,
        color: area.color ?? null,
        areaSource: area.areaSource ?? null,
        areaGroup: area.areaGroup ?? null,
      }
      if (!originalIds.has(area.id)) {
        tasks.push(async () => createHuntingGround(groupId, body, await getToken()))
      } else {
        const original = originals.find((a) => a.id === area.id)!
        if (hasAreaChanged(area, original)) {
          tasks.push(async () => updateHuntingGround(groupId, area.id, body, await getToken()))
        }
      }
    }

    for (const original of originals) {
      if (!currentIds.has(original.id)) {
        tasks.push(async () => deleteHuntingGround(groupId, original.id, await getToken()))
      }
    }
  }

  return tasks
}

/** Ids of the EXISTING (already-stored) pictures still present on a pin. */
function keptImageIds(pin: MapObject): string[] {
  return (pin.photos ?? []).flatMap((img) => ('id' in img ? [img.id] : []))
}

/** The newly-staged pictures on a pin — bytes to upload, no id yet. */
function newImageFiles(pin: MapObject): File[] {
  return (pin.photos ?? []).flatMap((img) => ('file' in img ? [img.file] : []))
}

/** True when the pin's picture set differs from what was loaded: a new file was
 * staged, or the surviving existing ids no longer match the originals (a
 * picture was removed or replaced). Order changes are not treated as a change —
 * the editor offers no reorder, and the stored set is what matters. */
function haveImagesChanged(current: MapObject, original: MapObject): boolean {
  if (newImageFiles(current).length > 0) return true
  const kept = new Set(keptImageIds(current))
  const originalIds = keptImageIds(original)
  if (kept.size !== originalIds.length) return true
  return originalIds.some((id) => !kept.has(id))
}

/**
 * Resolve a pin's final `images` id list and persist it: upload every newly
 * staged picture (minting an id each), append those ids to the surviving
 * existing ids, and send the whole list — a deliberate whole-list replace that
 * matches how the mobile client writes the field. Any existing id that was
 * dropped (a removed or replaced picture) has its Storage object deleted after
 * the write, so a failed write never orphans the id off a picture it still
 * references. Uploads run inside this task so they are throttled by the same
 * bounded pool as every other map write.
 *
 * Returns the task rather than being one, so that `uploaded` survives across
 * retries of the returned thunk. Each picture's bytes are uploaded once and the
 * minted id reused on every later attempt: `uploadMapPinImage` mints a fresh
 * UUID per call and writes straight to Storage, so re-uploading on a retry
 * would leave the previous attempt's blobs referenced by nothing — and
 * `removedIds` cannot clean them up, because it only covers ids dropped from
 * the pin as it was loaded. Retrying the write is now the expected path on a
 * large save, which is what turns that from a rare leak into a routine one.
 * An upload that itself failed is left uncached, so it is retried normally.
 */
function persistPinImagesTask(
  pin: MapObject,
  removedIds: string[],
  write: (images: string[]) => Promise<unknown>,
  copyKept?: (ids: string[]) => Promise<string[]>,
): SaveTask {
  const files = newImageFiles(pin)
  const uploaded = new Array<string | undefined>(files.length)
  // Memoised like `uploaded`: each copy mints new objects, so a retried write
  // must reuse the first copy's ids rather than copy again.
  let kept: string[] | undefined
  return async () => {
    const keptIds = keptImageIds(pin)
    kept ??= copyKept && keptIds.length > 0 ? await copyKept(keptIds) : keptIds
    await Promise.all(
      files.map(async (file, i) => {
        uploaded[i] ??= await uploadMapPinImage(file)
      }),
    )
    const images = [...kept, ...(uploaded as string[])]
    await write(images)
    await Promise.all(removedIds.map((id) => deleteMapPinImage(id)))
  }
}

/**
 * The pin fields written only when present: a `custom` pin's glyph and colour.
 * Mirrors mobile's own write rule for the same two fields — the API merges
 * the create/update body onto the stored document, so a key this omits is a
 * key the write leaves untouched, and a save that carries no colour or symbol
 * (because the pin has none, or is not a `custom` pin) can never blank out
 * one already stored.
 */
function pinWireFields(pin: MapObject): { customSymbol?: string; color?: number } {
  return {
    ...(pin.customSymbol ? { customSymbol: pin.customSymbol } : {}),
    ...(pin.color ? { color: hexToArgbInt(pin.color) } : {}),
  }
}

/** Build the (deferred) create/update/delete tasks for team map pins. */
export function buildPinSaveTasks(
  teamId: string,
  currentObjects: MapObject[],
  originalPins: MapObject[],
  getToken: () => Promise<string>,
): SaveTask[] {
  // Trail cameras are dropped from BOTH sides before diffing. They are camera
  // pins on a map but not `mapPins` documents — a camera's position lives in the
  // Burrel database and there is no map-pin write that could move it — so the
  // read-only team map merges them in while the editor is handed the bare pins.
  // Excluding them here makes that separation unable to corrupt data if the two
  // lists ever meet: a camera reaching `current` would otherwise be CREATED as a
  // real map pin (it has no match in `original`), duplicating every camera on
  // the ground as an ordinary pin on every save. Both sides, not just `current`,
  // so a stored pin that happened to carry this id prefix goes untouched rather
  // than being read as deleted.
  // Private pins are excluded on the same grounds as cameras, and on both sides
  // for the same reason: the team endpoints refuse a pin the team does not own,
  // so one reaching `current` would fail its save with a 403, and one reaching
  // `originals` alone would be read as a team pin the user deleted. The editor
  // is not fed them today — only the read-only team map merges them in — and
  // filtering here is what keeps that separation from being load-bearing.
  const teamOwned = (p: MapObject) => !isTrailCameraPin(p) && !isPrivatePin(p)
  const pins = currentObjects.filter(teamOwned)
  const originals = originalPins.filter(teamOwned)
  const originalIds = new Set(originals.map((p) => p.id))
  const currentIds = new Set(pins.map((p) => p.id))

  const tasks: SaveTask[] = []

  for (const pin of pins) {
    if (!originalIds.has(pin.id)) {
      // Memoised across retry attempts, the same way persistPinImagesTask
      // memoises an upload's minted id: `withRetry` wraps this whole thunk,
      // and it now spans TWO non-idempotent calls (create the pin, then file
      // it) instead of one. A transient failure on the SECOND call (e.g. a
      // 429 from fileTeamMapPin — expected on a save large enough to spend the
      // rate limiter's budget) must not re-run the first, which already
      // succeeded; re-running it would create a second, duplicate pin.
      let created: TeamMapPinDto | undefined
      tasks.push(
        persistPinImagesTask(pin, [], async (images) => {
          created ??= await createTeamMapPin(
            teamId,
            {
              name: pin.label || null,
              type: pin.type,
              latitude: pin.lat,
              longitude: pin.lon,
              images,
              ...pinWireFields(pin),
              // A copied map's pins carry their notes into the new ground.
              ...(pin.description ? { description: pin.description } : {}),
            },
            await getToken(),
          )
          // A brand-new pin has no filing to diff against — there is no
          // "original" row buildPinFilingTasks could compare it to — so a
          // folderId set on it in the editor is filed here, once the create
          // has minted the server id fileTeamMapPin needs. By the time this
          // runs, `pin.folderId` has already been remapped (in saveTeamMap)
          // from any temporary folder id the editor minted to the folder's
          // real server id.
          if (pin.folderId) {
            await fileTeamMapPin(teamId, created.id, pin.folderId, await getToken())
          }
          return created
        },
        // A pin copied from another team's map still names that pin's pictures;
        // they are duplicated first so the two pins never share an object.
        pin.copiedFromPinId
          ? async (ids) => copyTeamMapPinImages(teamId, pin.copiedFromPinId!, ids, await getToken())
          : undefined),
      )
    } else {
      const original = originals.find((p) => p.id === pin.id)!
      const imagesChanged = haveImagesChanged(pin, original)
      const descriptionChanged = (pin.description ?? '') !== (original.description ?? '')
      if (
        original.lat !== pin.lat ||
        original.lon !== pin.lon ||
        original.label !== pin.label ||
        original.type !== pin.type ||
        original.color !== pin.color ||
        original.customSymbol !== pin.customSymbol ||
        descriptionChanged ||
        imagesChanged
      ) {
        const kept = new Set(keptImageIds(pin))
        const removedIds = keptImageIds(original).filter((id) => !kept.has(id))
        tasks.push(
          persistPinImagesTask(pin, removedIds, async (images) =>
            updateTeamMapPin(
              teamId,
              pin.id,
              {
                name: pin.label || null,
                type: pin.type,
                latitude: pin.lat,
                longitude: pin.lon,
                images,
                ...pinWireFields(pin),
                // Sent only when edited, so a save that did not touch the note
                // cannot overwrite one the app changed after this page loaded.
                ...(descriptionChanged ? { description: pin.description ?? '' } : {}),
              },
              await getToken(),
            ),
          ),
        )
      }
    }
  }

  for (const original of originals) {
    if (!currentIds.has(original.id)) {
      tasks.push(async () => deleteTeamMapPin(teamId, original.id, await getToken()))
    }
  }

  return tasks
}

/**
 * Build the (deferred) filing tasks for team map pins whose folder membership
 * changed. Separate from {@link buildPinSaveTasks} because filing a pin is a
 * single-field write through its own endpoint (`fileTeamMapPin` — see the doc
 * comment on it in teamMapPinsApi.ts for why: the create/update body cannot
 * express "unfile", since a null there means "leave unchanged"), not part of
 * the create/update body. A brand-new pin's filing is NOT built here — it has
 * no `original` row to diff against, so it is chained inside its own create
 * task in `buildPinSaveTasks` instead, once that create has minted the server
 * id `fileTeamMapPin` needs.
 *
 * `currentObjects` must already have had any temporary folder id (minted by
 * the editor for a folder created in this same save) remapped to the real
 * server id — see the remap in `saveTeamMap` — or an existing pin filed into a
 * brand-new folder would be sent a folderId the API has never heard of.
 */
export function buildPinFilingTasks(
  teamId: string,
  currentObjects: MapObject[],
  originalPins: MapObject[],
  getToken: () => Promise<string>,
): SaveTask[] {
  // Same team-ownership filter as buildPinSaveTasks, for the same reasons: a
  // camera has no folderId a write could touch, and a private pin's folder is
  // the viewer's own collection, not this team's — the team endpoint would
  // 403 it.
  const teamOwned = (p: MapObject) => !isTrailCameraPin(p) && !isPrivatePin(p)
  const pins = currentObjects.filter(teamOwned)
  const originals = originalPins.filter(teamOwned)
  const originalById = new Map(originals.map((p) => [p.id, p]))

  const tasks: SaveTask[] = []
  for (const pin of pins) {
    const original = originalById.get(pin.id)
    if (!original) continue // new pin — filed inside its own create task instead
    if ((pin.folderId ?? null) !== (original.folderId ?? null)) {
      tasks.push(async () => fileTeamMapPin(teamId, pin.id, pin.folderId ?? null, await getToken()))
    }
  }
  return tasks
}

/** True when any web-owned field of a line differs from what was loaded.
 * Geometry and every editable attribute count; `capStart` is deliberately
 * excluded because web never writes it (see mapLineDtoMappers) so it can
 * never be the thing that changed. Normalizing the optional fields against
 * their effective defaults (as hasAreaChanged does for border colour) keeps
 * a round-tripped-but-unmodified line from reading as changed and getting a
 * pointless re-PUT on every save. */
export function hasLineChanged(current: GeoLine, original: GeoLine): boolean {
  if (current.kind !== original.kind) return true
  if ((current.name ?? '') !== (original.name ?? '')) return true
  if ((current.description ?? '') !== (original.description ?? '')) return true
  if ((current.color ?? '') !== (original.color ?? '')) return true
  if ((current.width ?? LINE_DEFAULT_WIDTH) !== (original.width ?? LINE_DEFAULT_WIDTH)) return true
  if ((current.style ?? 'solid') !== (original.style ?? 'solid')) return true
  if (current.points.length !== original.points.length) return true
  return current.points.some(
    (p, i) => p.lat !== original.points[i].lat || p.lon !== original.points[i].lon,
  )
}

/**
 * Build the (deferred) create/update/delete tasks for team map lines.
 *
 * SAFETY: `originalLines` must be exactly the documents web loaded and
 * showed the user — never a re-query, never anything wider. The shared
 * `lines` collection also holds `pinType: 'trail'` documents: GPS tracks
 * recorded on mobile or imported from GPX. Deletion here is "in
 * originalLines, absent from currentLines" — if originalLines were ever
 * widened past what was actually loaded, that diff would delete a hunter's
 * recorded trail with no error and no undo. This function only ever reads
 * its two array parameters; it must never fetch or query on its own.
 */
export function buildLineSaveTasks(
  teamId: string,
  currentLines: GeoLine[],
  originalLines: GeoLine[],
  getToken: () => Promise<string>,
): SaveTask[] {
  const originalIds = new Set(originalLines.map((l) => l.id))
  const currentIds = new Set(currentLines.map((l) => l.id))

  const tasks: SaveTask[] = []

  for (const line of currentLines) {
    const body = geoLineToWriteBody(line)
    if (!originalIds.has(line.id)) {
      tasks.push(async () => createTeamMapLine(teamId, body, await getToken()))
    } else {
      const original = originalLines.find((l) => l.id === line.id)!
      if (hasLineChanged(line, original)) {
        tasks.push(async () => updateTeamMapLine(teamId, line.id, body, await getToken()))
      }
    }
  }

  for (const original of originalLines) {
    if (!currentIds.has(original.id)) {
      tasks.push(async () => deleteTeamMapLine(teamId, original.id, await getToken()))
    }
  }

  return tasks
}

export interface SaveTeamMapParams {
  teamId: string
  mapState: {
    boundaryAreas: GeoArea[]
    subAreaPolygons: GeoArea[]
    forbiddenPolygons: GeoArea[]
    objects: MapObject[]
  }
  originalAreas: HuntingGroundAreaDto[]
  originalPins: MapObject[]
  /**
   * CLUB-528: the map's lines, current and loaded, as ONE optional object
   * rather than two independent optional fields. That shape is deliberate: a
   * caller can supply both `current` and `original` together, or omit the
   * whole field, but cannot supply `current` while leaving `original`
   * missing. Splitting them (as two separate optional properties) makes that
   * exact wrong half-supplied state reachable — omit only `original` and
   * `buildLineSaveTasks` sees an empty original set, so every current line
   * looks new and gets POSTed as a fresh document. `createTeamMapLine` takes
   * no id, so that isn't a no-op the way an omitted `boundaryAreas` field
   * would be: it silently DUPLICATES every line on the map, on every save.
   * Collapsing to one field makes that state structurally impossible —
   * there's only "both" or "neither" to supply. Omitting the field entirely
   * is always safe: no current lines to create, no originals to delete.
   */
  lines?: { current: GeoLine[]; original: GeoLine[] }
  /**
   * The team's pin folders, current and loaded, as ONE optional object —
   * the same both-or-neither shape as {@link lines} above, and for the same
   * reason: a caller can supply both `current` and `original` together, or
   * omit the whole field, but splitting them into two independent optional
   * properties would make the wrong half-supplied state reachable — omit only
   * `original` and every current folder would look new and get POSTed as a
   * fresh document (`createTeamPinFolder` takes no id, so that duplicates
   * every folder on every save, the same failure `lines` guards against).
   * Omitting the field entirely is always safe: no current folders to create,
   * rename or delete. A pin's own filing (`buildPinFilingTasks`) is diffed
   * off `mapState.objects`/`originalPins` regardless — filing an existing pin
   * needs no folder list, only the two folderId values being compared — so
   * that keeps working even when this field is left out.
   */
  folders?: { current: PinFolder[]; original: PinFolder[] }
  /**
   * Resolved once per attempt, never captured for the whole save. A save is now
   * bounded by the rate limiter rather than the network — roughly ceil(N/100)
   * minutes for N objects — which is long enough for a token captured up front
   * to expire mid-save. An expired token answers 401, which is not transient,
   * so every remaining write would be dropped: this PR's own failure mode
   * re-entering through a different status code. Firebase returns the cached
   * token until it is close to expiry, so calling per attempt is cheap.
   */
  getToken: () => Promise<string>
  /** Overrides, mainly for tests. */
  concurrency?: number
  retry?: RetryOptions
}

/**
 * Persist all area + pin + line + folder edits for a team map. Areas, pins and
 * lines run through a single bounded pool, throttled rather than firing
 * unbounded `Promise.all` bursts in parallel (CLUB-195). Folders sit outside
 * that pool, sequential and phased around it — see the two comments below for
 * why each direction has to be where it is. Every write (pooled or
 * sequential) is wrapped in transient-failure retry; any failure throws and
 * the caller leaves the map dirty rather than mark a partially-applied save
 * as clean (see the SUCCESS-path comment on MapEditorRoute.handleSave for why
 * that matters more than it looks).
 */
export async function saveTeamMap(params: SaveTeamMapParams): Promise<void> {
  const { current: currentLines = [], original: originalLines = [] } = params.lines ?? {}
  const { current: currentFoldersAll = [], original: originalFoldersAll = [] } = params.folders ?? {}
  // Filtered on BOTH sides, for the same reason buildPinSaveTasks filters
  // isPrivatePin on both sides of its own diff: PinFolder's own doc comment
  // warns a team folder and the viewer's private folder can share an id, so a
  // private folder reaching this diff would either be (falsely) created or
  // renamed as a team folder, or (falsely) deleted as a team folder the
  // editor never actually removed. MapEditorRoute today only ever feeds team
  // folders in here, so this is a belt-and-braces guard against a caller that
  // doesn't — not a path any current caller exercises.
  const currentFolders = currentFoldersAll.filter((f) => !f.private)
  const originalFolders = originalFoldersAll.filter((f) => !f.private)

  // Phase 1 — folders IN, sequential, before the pool. A pin filed (in the
  // editor) into a brand-new folder points at the id the editor minted for it
  // client-side (e.g. `pf…`); that folder has to exist under its real server
  // id before phase 2 below can file anything into it. Sequential rather than
  // pooled: runPooled has no result channel back to the caller, and a save
  // touches at most a handful of folders, which does not need one.
  const idMap = new Map<string, string>()
  const originalFoldersById = new Map(originalFolders.map((f) => [f.id, f]))
  for (const folder of currentFolders) {
    const original = originalFoldersById.get(folder.id)
    if (!original) {
      const created = await withRetry(
        async () => createTeamPinFolder(params.teamId, folder.name, await params.getToken()),
        params.retry,
      )
      idMap.set(folder.id, created.id)
    } else if (original.name !== folder.name) {
      await withRetry(
        async () => renameTeamPinFolder(params.teamId, folder.id, folder.name, await params.getToken()),
        params.retry,
      )
    }
  }

  // Pins' folderId remapped through the id map above before diffing/filing,
  // so a pin filed into a folder created in THIS save (still carrying the
  // editor's temporary id) is diffed and filed against the folder's real
  // server id instead.
  const remappedObjects = params.mapState.objects.map((obj) => {
    const mapped = obj.folderId ? idMap.get(obj.folderId) : undefined
    return mapped ? { ...obj, folderId: mapped } : obj
  })

  // Phase 2 — the existing pool, now also carrying pin-filing tasks
  // (buildPinFilingTasks) alongside the area/pin/line create-update-delete
  // tasks. A brand-new pin's filing is not among them — it is chained inside
  // its own create task in buildPinSaveTasks instead, since it needs the
  // server id that create mints.
  const tasks: SaveTask[] = [
    ...buildAreaSaveTasks(params.teamId, params.mapState, params.originalAreas, params.getToken),
    ...buildPinSaveTasks(params.teamId, remappedObjects, params.originalPins, params.getToken),
    ...buildPinFilingTasks(params.teamId, remappedObjects, params.originalPins, params.getToken),
    ...buildLineSaveTasks(params.teamId, currentLines, originalLines, params.getToken),
  ]
  await runPooled(
    tasks.map((task) => () => withRetry(task, params.retry)),
    params.concurrency ?? MAP_SAVE_CONCURRENCY,
  )

  // Phase 3 — folders OUT, sequential, after the pool. The API refuses to
  // delete a folder that still holds pins (422). By the time the editor
  // recorded a folder's deletion, its state had already unfiled or deleted
  // every pin that was in it — phase 2 above is what persists THAT — so this
  // delete has to run only once the pool has fully drained, or it could race
  // ahead of the very filing/delete write that empties the folder.
  //
  // That 422 also fires when the folder still holds LINES, and — unlike
  // pins — the editor cannot always know that in advance: a line's folderId
  // is never round-tripped to the wire (mapLineDtoMappers carries no such
  // field), so a folder holding lines from an EARLIER session reads as empty
  // in the editor's own "this folder holds N items" count, which only sees
  // what made it into this session's in-memory state. The editor can
  // therefore offer a delete the API is going to refuse, though not one it
  // knows to refuse.
  //
  // So this one delete's 422 is caught and swallowed rather than thrown: the
  // folder simply stays on the server, which the success-path refetch below
  // then shows the editor honestly, without inventing an error message the
  // user could not act on (there is nothing to click "fix" on — the folder
  // is not empty, full stop). Throwing instead would fail the WHOLE save —
  // discarding everything phases 1 and 2 already persisted for no better
  // reason than one folder the user thought was already empty. Any other
  // failure (a genuine 500, say) still throws, same as every other write in
  // this function.
  const currentFolderIds = new Set(currentFolders.map((f) => f.id))
  for (const folder of originalFolders) {
    if (!currentFolderIds.has(folder.id)) {
      try {
        await withRetry(
          async () => deleteTeamPinFolder(params.teamId, folder.id, await params.getToken()),
          params.retry,
        )
      } catch (err) {
        if (!(err instanceof ApiError && err.status === 422)) throw err
      }
    }
  }
}
